import type { PageBreadcrumbItem } from "@/components/layout/PageLayout";
import type { RecordTab } from "@/components/templates/parts";
import { reportDefinitionByKey } from "@/lib/reports/report-definitions";
import type { ReportAdapterKey } from "@/lib/reports/adapters";

/**
 * A report as a T2 record (PAGE_TEMPLATES.md, page map: "a report is a record: parameters,
 * results, history"). Results carries the parameters that shape it; Schedule and History are
 * the record's other sections. Tabs are routes (NAVIGATION_AND_STATES.md § Deep links):
 * `/reports/:key` is Results, `/reports/:key/schedule` and `/reports/:key/history` the rest.
 */
export const REPORT_RECORD_SECTIONS = ["results", "schedule", "history"] as const;
export type ReportRecordSection = (typeof REPORT_RECORD_SECTIONS)[number];

const SECTION_LABELS: Record<ReportRecordSection, string> = {
  results: "Results",
  schedule: "Schedule",
  history: "History",
};

/**
 * The record's in-page text links (rail, alert slot, no-permission note), on `TouchTargetLink`:
 * `--foreground` text, underlined so it reads as a link. Not the amber `text-primary`, which is
 * about 2.2:1 on a light card (ACCESSIBILITY.md § Colour and contrast: body text 4.5:1).
 */
export const REPORT_RECORD_LINK_CLASS = "text-sm font-medium underline";

/** The report library: the collection a report is opened from (MODULE_MAPS.md § Reports). */
export const REPORTS_HOME_HREF = "/reports";
export const REPORTS_LIBRARY_HREF = "/reports?view=library";

/** The report record's own path, without a section: `/reports/:key`. */
export function reportRecordPath(reportKey: string): string {
  return `/reports/${encodeURIComponent(reportKey)}`;
}

/** A section's URL. `search` carries the report's parameters, so they survive a tab change. */
export function reportSectionHref(reportKey: string, section: ReportRecordSection, search = ""): string {
  const base = reportRecordPath(reportKey);
  return `${section === "results" ? base : `${base}/${section}`}${search}`;
}

/**
 * The section a URL's `:section` segment names. No segment is Results. Anything else, including
 * an explicit `results` (Results lives at the record's own path), is not a section URL: the
 * caller redirects it to the record, as PersonProfile does for `/details`.
 */
export function resolveReportSection(segment: string | undefined): ReportRecordSection | null {
  if (segment === undefined) return "results";
  return segment === "schedule" || segment === "history" ? segment : null;
}

/** The record's tabs. A count shows only when it is known (RecordTab: omit rather than show 0 for unknown). */
export function reportRecordTabs(
  reportKey: string,
  search: string,
  counts: { history?: number } = {},
): RecordTab[] {
  return REPORT_RECORD_SECTIONS.map((section) => ({
    id: section,
    label: SECTION_LABELS[section],
    href: reportSectionHref(reportKey, section, search),
    ...(section === "history" && counts.history !== undefined ? { count: counts.history } : {}),
  }));
}

/** `Reports / Library / {report}` (NAVIGATION_AND_STATES.md § Breadcrumbs: Module / Collection / Record). */
export function reportRecordBreadcrumbs(name: string): PageBreadcrumbItem[] {
  return [
    { label: "Reports", href: REPORTS_HOME_HREF },
    { label: "Library", href: REPORTS_LIBRARY_HREF },
    { label: name },
  ];
}

const titleCase = (value: string) => value.replace(/\b\w/g, (char) => char.toUpperCase());

/** The report's name: its definition's, or its key in words for a report Forge doesn't have. */
export function reportRecordName(reportKey: string): string {
  return reportDefinitionByKey[reportKey as ReportAdapterKey]?.name ?? titleCase(reportKey.replace(/-/g, " "));
}
